import ClassNames from '@streamloots/classnames';
import { PaymentMethods } from '@streamloots/streamloots-types';
import { useReactAlert } from 'components/react-alert-provider';
import { OrderedFeedContentItem, OrderedItemType, PageFeed } from 'model/indexTS';
import { useEffect } from 'react';
import { TransparentCheckout } from 'scenes/checkout';
import { currencyManager } from 'services/culture';
import { PageState } from 'services/page';
import trackEventManager from 'utils/event-manager';
import { useTranslation } from 'utils/translation';
import theme from './checkout-unlock.scss';

const classNames = ClassNames(theme);

export interface CheckoutUnlockProps {
  page: PageState;
  feed: PageFeed;
  onClose: () => void;
}

export const CheckoutUnlock = ({ page, feed, onClose }: CheckoutUnlockProps): JSX.Element => {
  const { t } = useTranslation('pageFeed');
  const { showSuccess } = useReactAlert();

  useEffect(() => {
    trackEventManager.feedSectionViewed({
      location: 'feed content',
      section: 'paid content checkout',
    });
  }, []);

  const onSuccess = () => {
    onClose();
    showSuccess(t('pageFeed:unlockCheckout.success'));
  };

  const handleFeedClicked = (button: 'init paid content payment - paypal' | 'init paid content payment - stripe') => {
    trackEventManager.feedClicked({
      location: 'paid content checkout',
      button,
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
  };

  const handleTrackPaymentMethodChange = (value: PaymentMethods) => {
    if (value === PaymentMethods.PAYPAL) {
      handleFeedClicked('init paid content payment - paypal');
    }

    if (value === PaymentMethods.STRIPE) {
      handleFeedClicked('init paid content payment - stripe');
    }
  };

  if (!feed.price || !feed.currency || !page) {
    return <div className={classNames('checkout-unlock__error')}>{t('pageFeed:unlockCheckout.block')}</div>;
  }

  const orderItem: OrderedFeedContentItem[] = [
    {
      quantity: 1,
      type: OrderedItemType.FeedContent,
      amount: feed.price,
      feedId: feed._id,
    },
  ];

  return (
    <TransparentCheckout
      orderedItems={orderItem}
      currency={feed.currency}
      pageId={page._id}
      open
      onClose={onClose}
      title={t('pageFeed:unlockCheckout.description', {
        slug: page.slug,
      })}
      isOwner={page.isOwner}
      onTrackPaymentMethodChange={handleTrackPaymentMethodChange}
      onSuccess={onSuccess}
      titleClassName={classNames('checkout-unlock__header')}
    >
      <div className={classNames('checkout-unlock')}>
        <h3 className={classNames('checkout-unlock__title')}>
          {t('pageFeed:unlockCheckout.title', { slug: page.slug })}
        </h3>
        <div className={classNames('checkout-unlock__amount')}>
          {currencyManager.getFormattedPrice(feed.price, feed.currency)}
        </div>
      </div>
    </TransparentCheckout>
  );
};
